import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FileUploadService} from '../../services/file-upload-service/file-upload.service';
import {FileUploadStatus} from '../../model/file-upload-status';
import {BehaviorSubject} from 'rxjs';
import {BinaryData} from '../../model/proposal/binary-data';
import {UploadLocation} from '../../enum/upload-location';
import {CustomDialogService} from '../../services/custom-dialog/custom-dialog.service';
import {selectUserRoles} from '../../store/auth/selectors/auth.selectors';
import {Roles} from '../../enum/roles';
import {Store} from '@ngrx/store';

@Component({
    selector: 'app-upload',
    templateUrl: './upload.component.html',
    styleUrls: ['./upload.component.scss', '../../../styles/form-input.scss'],
    standalone: false
})
export class UploadComponent implements OnInit {
  @Output('updateAttachedFilesInParent') attachedFiles = new EventEmitter<BinaryData[]>();
  @Output() uploadInProgress = new EventEmitter<boolean>();

  @Input() uploadLocation: UploadLocation = null;
  @Input() hasError: boolean = false;


  readonly ACCEPTED_FILE_TYPES = [
    'application/pdf', // PDF
    'text/plain', // Text
    'application/msword', // Word .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // Word .docx
    'text/csv', // CSV
    'application/vnd.ms-excel', // Excel .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // Excel .xlsx
    'image/jpeg', 'image/jpg', 'image/png', // Images
    'video/mp4' // Videos MP4
  ];

  readonly ACCEPTED_FILE_EXTENSIONS = [
    '.pdf',
    '.txt',
    '.doc', '.docx',
    '.csv',
    '.xls', '.xlsx',
    '.jpg', '.jpeg', '.png',
    '.mp4'
  ];

  fileUploadStatusOfComponent$ = new BehaviorSubject<FileUploadStatus[]>([]);

  private fileUploadStatusOfComponent: FileUploadStatus[] = [];
  fileUploadInProgress$ = new BehaviorSubject<boolean>(false);
  private fileToBeUploaded: File[] = [];

  constructor(public fileUploadService: FileUploadService, readonly customDialogService: CustomDialogService, public store: Store) {
  }

  ngOnInit(): void {
    this.fileUploadService.fileUploadStatus$.subscribe(allFileUploads => {
      this.fileUploadStatusOfComponent = allFileUploads.filter(fileUploadStatus =>
        this.fileUploadStatusOfComponent.filter(componentStatus => componentStatus.internalId == fileUploadStatus.internalId).length > 0
      );

      // Update component-specific list
      this.fileUploadStatusOfComponent$.next(this.fileUploadStatusOfComponent);

      // Update if upload is in Progress
      this.fileUploadInProgress$.next(
        this.fileUploadStatusOfComponent.find(fileUploadStatus => fileUploadStatus.progress != 100)
        != undefined);

      this.attachedFiles.emit(this.fileUploadStatusOfComponent
        .filter(fileUploadStatus => fileUploadStatus.progress == 100)
        .map(fileUploadStatus => {
          const binaryData = new BinaryData({
            id: fileUploadStatus.id,
            name: fileUploadStatus.fileName,
            sizeInBytes: fileUploadStatus.fileSize,
            secret: fileUploadStatus.fileSecret,
            hash: fileUploadStatus.fileHash,
            type: fileUploadStatus.fileType,
            createDate: Date.now(),
            uploadLocation: fileUploadStatus.uploadLocation
          });
          // called only if user role is association admin because this fun is also used by broker and insurer
          this.store.select(selectUserRoles).subscribe(roles => {
            if (roles.includes(Roles.ROLE_ASSOCIATION_ADMIN))
              this.fileUploadService.saveBinaryData(binaryData);
          });

          return binaryData;
        })
      );
    });
  }

  public deleteFileUploadStatusFromFrontendOnly(internalId: string) {
    this.fileUploadStatusOfComponent = this.fileUploadStatusOfComponent.filter(fus => fus.internalId != internalId);
    this.fileUploadStatusOfComponent$.next(this.fileUploadStatusOfComponent);
    this.fileUploadService.clearFileFromFileUploadStatusArray(internalId);
  }

  public retryFileUpload(fileUploadStatus: FileUploadStatus) {
    this.deleteFileUploadStatusFromFrontendOnly(fileUploadStatus.internalId);
    let fileToBeUploadedAgain = this.fileToBeUploaded.filter(file => file.name === fileUploadStatus.fileName);
    this.fileToBeUploaded = this.fileToBeUploaded.filter(file => file.name !== fileUploadStatus.fileName);
    this.submitFileUpload(fileToBeUploadedAgain as unknown as FileList);
  }

  private isFileSupported(file: File): boolean {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

    return this.ACCEPTED_FILE_TYPES.includes(file.type) ||
           this.ACCEPTED_FILE_EXTENSIONS.includes(fileExtension);
  }

  async submitFileUpload(files: FileList, input?: HTMLInputElement): Promise<any> {
    this.uploadInProgress.emit(true);
    // Convert FileList to an array for iteration
    const fileListArray = Array.from(files);

    // return immediately when the input is undefined or has no files in the file list
    if (fileListArray.length === 0) {
      this.uploadInProgress.emit(false);
      return;
    }

    for (const file of fileListArray) {
      if (!this.isFileSupported(file)) {
        this.customDialogService.unsupportedFileError();
        continue;
      }

      const isDuplicate = this.fileUploadStatusOfComponent.some(fileUploadStatus =>
        fileUploadStatus.fileName === file.name
      );

      if (isDuplicate) {
        this.customDialogService.duplicateFileError();
        continue;
      }

      let fileUploadStatus = new FileUploadStatus(
          file.name,
          this.fileUploadService.getIconSourceForFilename(file.name),
          file.size,
          file.type,
          this.uploadLocation
      );
      this.fileUploadStatusOfComponent.push(fileUploadStatus);
      this.fileToBeUploaded.push(file);

      await this.fileUploadService.uploadFile(file, fileUploadStatus).then(() => {
        this.fileToBeUploaded = this.fileToBeUploaded.filter(file => file.name !== fileUploadStatus.fileName);
      })
        .catch(error => console.error("File upload error: ", error));
    }
    this.uploadInProgress.emit(false);

    // resets input so the same file upload will trigger the function again
    if (input) {
      input.value = '';
    }
  }

  public addPreUploadedFiles(preUploadedFiles: BinaryData[]) {
    preUploadedFiles.forEach(file => this.addPreUploadedFile(file));
  }

  private addPreUploadedFile(preUploadedFile: BinaryData) {
    let fileUploadStatus = new FileUploadStatus(
      preUploadedFile.name,
      this.fileUploadService.getIconSourceForFilename(preUploadedFile.name),
      preUploadedFile.sizeInBytes,
      preUploadedFile.type,
      this.uploadLocation,
      100,
      preUploadedFile.id,
      preUploadedFile.id,
      preUploadedFile.secret
      );
    this.fileUploadStatusOfComponent.push(fileUploadStatus);
    this.fileUploadService.addPreUploadedFile(fileUploadStatus);
  }

  clear() {
    this.fileUploadStatusOfComponent = [];
    this.fileUploadStatusOfComponent$.next([]);
    this.fileUploadInProgress$.next(false);
  }
}
